import { ColorType } from "./box-service";
import { ColorType2 } from "./model/color-type2";
import { CornerType } from "./model/corner-type";

const small = "small";
const tall = "tall";
const wide = "wide";
const large = "large";
const smallCorner = "small-corner";
const tallCorner = "tall-corner";
const tallWide = "tall/wide";
const smallTall = "small/tall";
const wideLarge = "wide/large";

type sizeType =
  | "small"
  | "tall"
  | "wide"
  | "large"
  | "small-corner"
  | "tall-corner";

export class Box {
  private _color: ColorType = "n";

  private _size: sizeType = small;

  private _index: number;

  private _back: boolean = false;

  private _door: boolean = false;

  private _drawerTop: boolean = false;

  private _drawerMiddle: boolean = false;

  private _drawerBottom: boolean = false;

  private _crossDivider: boolean = false;

  private _shelfCount: number = 0;

  private _direction: "r" | "l" | undefined;

  public constructor(index: number, color: ColorType) {
    this._index = index;
    this._color = color;
  }

  public canChangeColor(color: ColorType2) {
    if (this._size === tall && color.is(ColorType2.walnut)) {
      return false;
    }

    return true;
  }

  public changeColor(color: ColorType) {
    this._color = color;
  }

  public setSize(size: sizeType) {
    this._size = size;

    if (this.isCorner() && this._direction === undefined) {
      this._direction = "r";
    }

    if (!this.isCorner()) {
      this._direction = undefined;
    }
  }

  public hasBack(): boolean {
    return this._back;
  }

  public supportsBack() {
    // Small backs are not available for order in walnut
    return !(this._size === small && this._color === "w");
  }

  public addBack() {
    if (this.supportsBack()) {
      this._back = true;
    } else {
      this._back = false;
    }
  }

  public removeBack() {
    this._back = false;
  }

  public toggleBack() {
    if (this.supportsBack()) {
      if (this._back) {
        this.removeBack();
      } else {
        this.addBack();
      }
    } else {
      this.removeBack();
    }
  }

  public addDoor() {
    this._door = true;
    this._crossDivider = false;
  }

  public removeDoor() {
    this._door = false;
  }

  public toggleDoor() {
    if (this._door) {
      this.removeDoor();
    } else {
      this.addDoor();
    }
  }

  public addDrawerBottom() {
    this._drawerBottom = true;

    this._crossDivider = false;

    if (this.isCubeSizeSmall() || this.isCubeSizeWide()) {
      if (this.hasDrawerTop()) {
        this._shelfCount = 0;
      } else {
        this._shelfCount = 1;
      }
    } else {
      if (this.hasDrawerMiddle()) {
        if (this.hasDrawerTop()) {
          this._shelfCount = 0;
        } else {
          this._shelfCount = 1;
        }
      } else {
        if (this.hasDrawerTop()) {
          this._shelfCount = 1;
        } else {
          if (this.getShelfCount() > 2) {
            this._shelfCount = 2;
          }
          if (this.getShelfCount() < 1) {
            this._shelfCount = 1;
          }
        }
      }
    }
  }

  public removeDrawerBottom() {
    this._drawerBottom = false;
  }

  public toggleDrawerBottom() {
    if (this._drawerBottom) {
      this.removeDrawerBottom();
    } else {
      this.addDrawerBottom();
    }
  }

  public addDrawerMiddle() {
    this._crossDivider = false;

    if (this.isCubeSizeSmall() || this.isCubeSizeWide()) {
      this.addDrawerTop();
    } else {
      this._drawerMiddle = true;

      if (this.hasDrawerTop()) {
        this._shelfCount = 0;
      } else {
        this._shelfCount = 1;
      }
    }
  }

  public removeDrawerMiddle() {
    this._drawerMiddle = false;

    if (this.hasDrawerBottom()) {
      this.setShelves(1);
    } else {
      if (this.getShelfCount() > 1) {
        this.setShelves(1);
      }
    }
  }

  public toggleDrawerMiddle() {
    if (this._drawerMiddle) {
      this.removeDrawerMiddle();
    } else {
      this.addDrawerMiddle();
    }
  }

  public addDrawerTop() {
    this._drawerTop = true;

    this._crossDivider = false;

    if (this.isCubeSizeSmall() || this.isCubeSizeWide()) {
      this._shelfCount = 0;
    } else {
      if (this.hasDrawerMiddle()) {
        this._shelfCount = 0;
      } else {
        if (this.getShelfCount() > 1) {
          this._shelfCount = 1;
        }
      }
    }
  }

  public removeDrawerTop() {
    this._drawerTop = false;

    if (this.isCubeSizeSmall() || this.isCubeSizeWide()) {
      if (this.hasDrawerBottom()) {
        this.setShelves(1);
      } else {
        if (this.getShelfCount() > 1) {
          this.setShelves(1);
        }
      }
    } else {
      if (this.hasDrawerMiddle()) {
        this.setShelves(1);
      } else {
        if (this.getShelfCount() > 1) {
          this.setShelves(1);
        }
      }
    }
  }

  public toggleDrawerTop() {
    if (this._drawerTop) {
      this.removeDrawerTop();
    } else {
      this.addDrawerTop();
    }
  }

  public supportsCrossDivider(): boolean {
    // Tall and wide X (cross-divider) inserts are not available for order
    return this._size !== tall && this._size !== wide;
  }

  public hasCrossDivider(): boolean {
    return this._crossDivider;
  }

  public addCrossDivider() {
    if (this.supportsCrossDivider()) {
      if (this.isStandard()) {
        this._crossDivider = true;
        this._shelfCount = 0;
        this._drawerBottom = false;
        this._drawerMiddle = false;
        this._drawerTop = false;
        this._door = false;
      } else {
        this.removeCrossDivider();
      }
    } else {
      this.removeCrossDivider();
    }
  }

  public removeCrossDivider() {
    if (this.isStandard()) {
      this._crossDivider = false;
      this._shelfCount = 0;
      this._drawerBottom = false;
      this._drawerMiddle = false;
      this._drawerTop = false;
      this._door = false;
    } else {
      this._crossDivider = false;
    }
  }

  public toggleCrossDivider() {
    if (this.supportsCrossDivider()) {
      if (this._crossDivider) {
        this.removeCrossDivider();
      } else {
        this.addCrossDivider();
      }
    } else {
      this.removeCrossDivider();
    }
  }

  public setShelves(count: number) {
    if (this.isStandard()) {
      this._shelfCount = count;

      if (this._shelfCount >= 1) {
        this._drawerTop = false;
        this._drawerMiddle = false;
        this._drawerBottom = false;
      }

      this._crossDivider = false;
    }
  }

  public toggleShelf() {
    if (this.isCorner()) {
      return;
    }

    let max_shelves;

    if (this.isCubeSizeSmall() || this.isCubeSizeWide()) {
      max_shelves = 2;
    } else {
      max_shelves = 3;
    }

    const shelfCount = (this._shelfCount + 1) % (max_shelves + 1);

    this.setShelves(shelfCount);
  }

  public setDirectionRight() {
    if (this.isCorner()) {
      this._direction = "r";
    }
  }

  public setDirectionLeft() {
    if (this.isCorner()) {
      this._direction = "l";
    }
  }

  public toggleDirection() {
    if (!this.isCorner()) {
      return;
    }

    if (this._direction === "r") {
      this.setDirectionLeft();
    } else {
      this.setDirectionRight();
    }
  }

  public getColor(): ColorType {
    return this._color;
  }
  public getSize(): sizeType {
    return this._size;
  }
  public getIndex(): number {
    return this._index;
  }
  public getDrawersCount() {
    let count = 0;
    if (this._drawerBottom) {
      count++;
    }
    if (this._drawerMiddle) {
      count++;
    }
    if (this._drawerTop) {
      count++;
    }

    return count;
  }

  public hasDoor(): boolean {
    return this._door;
  }
  public hasDrawerTop(): boolean {
    return this._drawerTop;
  }
  public hasDrawerMiddle(): boolean {
    return this._drawerMiddle;
  }
  public hasDrawerBottom(): boolean {
    return this._drawerBottom;
  }

  public hasShelves() {
    return this._shelfCount > 0;
  }
  public getShelfCount(): number {
    return this._shelfCount;
  }
  public isDirectionRight(): boolean | undefined {
    return this._direction === undefined ? undefined : this._direction === "r";
  }
  public isDirectionLeft(): boolean | undefined {
    return this._direction === undefined ? undefined : this._direction === "l";
  }
  public getDirection(): "r" | "l" | undefined {
    return this._direction;
  }

  public isCorner() {
    return this._size === smallCorner || this._size === tallCorner;
  }
  public isStandard() {
    return !this.isCorner();
  }
  public isCubeSizeSmall() {
    return this._size === small;
  }
  public isCubeSizeSmallCorner() {
    return this._size === smallCorner;
  }
  public isCubeSizeWide() {
    return this._size === wide;
  }
  public isCubeSizeTall() {
    return this._size === tall;
  }
  public isCubeSizeTallCorner() {
    return this._size === tallCorner;
  }
  public isCubeSizeLarge() {
    return this._size === large;
  }
}
